import fabric from '@/lib/fabric';

export interface ImageFilterValues {
  brightness: number; // -1 .. 1
  contrast: number; // -1 .. 1
  blur: number; // 0 .. 1
  saturation: number; // -1 .. 1
  grayscale: boolean;
}

const DEFAULTS: ImageFilterValues = {
  brightness: 0,
  contrast: 0,
  blur: 0,
  saturation: 0,
  grayscale: false,
};

type FilterCtor = new (options?: Record<string, unknown>) => fabric.IBaseFilter;

function getFilterClass(
  name: 'Brightness' | 'Contrast' | 'Blur' | 'Saturation' | 'Grayscale'
): FilterCtor | null {
  const filters = fabric.Image?.filters as unknown as
    | Record<string, FilterCtor>
    | undefined;
  return filters?.[name] ?? null;
}

function findFilter(
  list: fabric.IBaseFilter[] | undefined,
  typeName: string
): fabric.IBaseFilter | undefined {
  return list?.find((f) => {
    const t = (f as { type?: string; constructor?: { name?: string } }).type;
    const n = (f as { constructor?: { name?: string } }).constructor?.name;
    return t === typeName || n === typeName;
  });
}

const FILTER_NAMES = [
  'Brightness',
  'Contrast',
  'Blur',
  'Saturation',
  'Grayscale',
] as const;

/** Read current filter slider values from a Fabric image. */
export function readImageFilters(img: fabric.Image): ImageFilterValues {
  const list = img.filters || [];
  const brightness = findFilter(list, 'Brightness') as
    | { brightness?: number }
    | undefined;
  const contrast = findFilter(list, 'Contrast') as { contrast?: number } | undefined;
  const blur = findFilter(list, 'Blur') as { blur?: number } | undefined;
  const saturation = findFilter(list, 'Saturation') as
    | { saturation?: number }
    | undefined;
  return {
    brightness: brightness?.brightness ?? 0,
    contrast: contrast?.contrast ?? 0,
    blur: blur?.blur ?? 0,
    saturation: saturation?.saturation ?? 0,
    grayscale: Boolean(findFilter(list, 'Grayscale')),
  };
}

/** Apply brightness / contrast / blur / saturation / grayscale. */
export function applyImageFilters(
  canvas: fabric.Canvas,
  img: fabric.Image,
  values: Partial<ImageFilterValues>
): ImageFilterValues {
  const current = readImageFilters(img);
  const next: ImageFilterValues = { ...current, ...values };
  const list = [...(img.filters || [])].filter((f) => {
    const t = (f as { type?: string; constructor?: { name?: string } }).type;
    const n = (f as { constructor?: { name?: string } }).constructor?.name;
    return !FILTER_NAMES.some((name) => t === name || n === name);
  });

  const Brightness = getFilterClass('Brightness');
  const Contrast = getFilterClass('Contrast');
  const Blur = getFilterClass('Blur');
  const Saturation = getFilterClass('Saturation');
  const Grayscale = getFilterClass('Grayscale');

  if (Brightness && Math.abs(next.brightness) > 0.001) {
    list.push(new Brightness({ brightness: next.brightness }));
  }
  if (Contrast && Math.abs(next.contrast) > 0.001) {
    list.push(new Contrast({ contrast: next.contrast }));
  }
  if (Blur && next.blur > 0.001) {
    list.push(new Blur({ blur: next.blur }));
  }
  if (Saturation && Math.abs(next.saturation) > 0.001) {
    list.push(new Saturation({ saturation: next.saturation }));
  }
  if (Grayscale && next.grayscale) {
    list.push(new Grayscale());
  }

  img.filters = list;
  img.applyFilters();
  canvas.requestRenderAll();
  return next;
}

export function resetImageFilters(canvas: fabric.Canvas, img: fabric.Image): void {
  applyImageFilters(canvas, img, DEFAULTS);
}
